import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Simple infinite scroll hook for Daily Phasing table
 * Uses append approach like the original useInfiniteScrollData
 */
export function useSimpleInfiniteScroll(sourceData, chunkSize = 30) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const containerRef = useRef(null);

    const loadData = useCallback(() => {
        if (!Array.isArray(sourceData) || sourceData.length === 0) return;
        setLoading(true);
        try {
            const newData = sourceData.slice(page * chunkSize, (page + 1) * chunkSize);
            setData(prevData => [...prevData, ...newData]);
            setHasMore(sourceData.length > (page + 1) * chunkSize);
            setPage(prev => prev + 1);
        } catch (error) {
            console.error('Error loading data:', error.message);
        } finally {
            setLoading(false);
        }
    }, [sourceData, page, chunkSize]);

    // Reset when sourceData changes
    const dataLengthRef = useRef(sourceData.length);
    const prevDataRef = useRef(sourceData);
    
    useEffect(() => {
        // Reset if data length changed or reference changed
        if (sourceData.length !== dataLengthRef.current || sourceData !== prevDataRef.current) {
            setData([]);
            setPage(0);
            setHasMore(true);
            dataLengthRef.current = sourceData.length;
            prevDataRef.current = sourceData;
        }
    }, [sourceData]);

    // Initial load after reset
    useEffect(() => {
        if (page === 0 && sourceData.length > 0) {
            loadData();
        }
    }, [page, sourceData.length, loadData]);

    // Handle scrolling
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight * 0.8 && !loading && hasMore) {
                loadData();
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [loadData, loading, hasMore]);

    return { data, containerRef, loading, hasMore };
}
